import React, { useState } from 'react';
import { adminService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Search, Star, MapPin, Clock, CheckCircle2, XCircle, Eye, AlertCircle, ChevronRight, UserCheck, Ban, Trash2, Wifi, WifiOff } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_TABS = ['ALL', 'PENDING', 'REVIEWING', 'APPROVED', 'REJECTED'];

const statusStyle = {
  PENDING:   'bg-amber-50 text-amber-700 border-amber-200',
  REVIEWING: 'bg-blue-50 text-blue-700 border-blue-200',
  APPROVED:  'bg-forest-50 text-forest-700 border-forest-200',
  REJECTED:  'bg-rust-50 text-rust-500 border-rust-200',
};

// ── Expert Detail / Action Modal ──────────────────────────────────────────────
const ExpertModal = ({ expert: initialExpert, onClose, onAction }) => {
  const [notes,  setNotes]  = useState('');
  const [acting, setActing] = useState(null);
  const [expert, setExpert] = useState(initialExpert);
  const [loadingDetail, setLoadingDetail] = useState(true);

  // Fetch full expert data on mount
  React.useEffect(() => {
    adminService.getExpertDetail(initialExpert.id)
      .then(res => {
        const d = res.data?.data?.expert ?? res.data?.data ?? res.data;
        if (d) setExpert(d);
      })
      .catch(() => {}) // fall back to list data
      .finally(() => setLoadingDetail(false));
  }, [initialExpert.id]);

  const name  = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
  const specs = Array.isArray(expert.specializations) ? expert.specializations
    : (() => { try { return JSON.parse(expert.specializations || '[]'); } catch { return []; } })();
  const quals = Array.isArray(expert.qualifications) ? expert.qualifications
    : (() => { try { return JSON.parse(expert.qualifications || '[]'); } catch { return []; } })();

  const handle = async (action) => {
    setActing(action);
    try {
      await onAction(expert.id, action, notes);
      onClose();
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-cream-100 px-6 py-4 flex items-center justify-between">
          <h3 className="font-serif text-xl text-forest-900">Expert Profile Review</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-5">
          {loadingDetail && (
            <div className="flex items-center gap-2 text-xs text-forest-400">
              <span className="w-3 h-3 border-2 border-forest-300 border-t-forest-600 rounded-full animate-spin" />
              Loading full profile...
            </div>
          )}
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-forest-700 rounded-2xl flex items-center justify-center text-cream-50 font-serif font-bold text-xl flex-shrink-0">
              {expert.user?.avatar
                ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover rounded-2xl" />
                : name[0]}
            </div>
            <div className="flex-1">
              <h4 className="font-serif text-lg font-semibold text-forest-900">{name}</h4>
              <p className="text-sm text-forest-500">{expert.user?.email}</p>
              {expert.user?.phone && <p className="text-xs text-forest-400">{expert.user.phone}</p>}
              <span className={`inline-block mt-2 text-[10px] font-semibold border px-2.5 py-1 rounded-full ${statusStyle[expert.expertStatus] ?? ''}`}>
                {expert.expertStatus}
              </span>
            </div>
            <div className="text-right">
              <div className="font-serif text-xl font-bold text-forest-900">
                {expert.hourlyRate ? `${expert.currency === 'USD' ? '$' : 'GH₵'}${expert.hourlyRate}/hr` : '—'}
              </div>
              <div className="text-xs text-forest-400">{expert.yearsOfExperience} yrs exp</div>
            </div>
          </div>

          {/* Bio */}
          {expert.bio && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Bio</p>
              <p className="text-sm text-forest-700 leading-relaxed bg-cream-50 rounded-xl p-4">{expert.bio}</p>
              <p className="text-xs text-forest-400 mt-1">{expert.bio.split(' ').length} words</p>
            </div>
          )}

          {/* Specializations */}
          {specs.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Specializations</p>
              <div className="flex flex-wrap gap-2">
                {specs.map(s => <span key={s} className="text-xs bg-forest-50 text-forest-700 border border-forest-100 px-2.5 py-1 rounded-full">{s}</span>)}
              </div>
            </div>
          )}

          {/* Qualifications */}
          {quals.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Qualifications</p>
              <ul className="space-y-1">
                {quals.map((q, i) => <li key={i} className="text-sm text-forest-600 flex items-start gap-2"><span className="text-forest-400 mt-0.5">•</span>{q}</li>)}
              </ul>
            </div>
          )}

          {/* Links */}
          {(expert.linkedinUrl || expert.websiteUrl) && (
            <div className="flex gap-3 flex-wrap">
              {expert.linkedinUrl && <a href={expert.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">LinkedIn ↗</a>}
              {expert.websiteUrl  && <a href={expert.websiteUrl}  target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Website ↗</a>}
            </div>
          )}

          {/* Intro video */}
          {expert.introVideoUrl && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Intro Video</p>
              <video src={expert.introVideoUrl} controls className="w-full rounded-xl max-h-52 bg-black" />
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3">
            {expert.timezone && (
              <div className="bg-cream-50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-0.5">Timezone</p>
                <p className="text-sm text-forest-700">{expert.timezone}</p>
              </div>
            )}
            {expert.levelScope && (
              <div className="bg-cream-50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-0.5">Level Scope</p>
                <p className="text-sm text-forest-700">{expert.levelScope === 'ALL_LEVELS' ? 'All Levels' : 'Specific Levels'}</p>
              </div>
            )}
            {expert.createdAt && (
              <div className="bg-cream-50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-0.5">Applied On</p>
                <p className="text-sm text-forest-700">{new Date(expert.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            )}
            {expert.user?.lastLoginAt && (
              <div className="bg-cream-50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-0.5">Last Login</p>
                <p className="text-sm text-forest-700">{new Date(expert.user.lastLoginAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
            )}
            {(() => {
              try {
                const types = JSON.parse(expert.sessionTypes || '[]');
                if (types.length) return (
                  <div className="bg-cream-50 rounded-xl p-3">
                    <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-0.5">Session Types</p>
                    <p className="text-sm text-forest-700">{types.join(', ')}</p>
                  </div>
                );
              } catch {}
              return null;
            })()}
            {expert._count && (
              <div className="bg-cream-50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-0.5">Platform Activity</p>
                <p className="text-sm text-forest-700">{expert._count.sessions ?? 0} sessions · {expert._count.bookings ?? 0} bookings</p>
              </div>
            )}
          </div>

          {/* Assigned levels */}
          {expert.expertLevels?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Assigned Levels</p>
              <div className="flex flex-wrap gap-2">
                {expert.expertLevels.map(el => (
                  <span key={el.id} className="text-xs px-2.5 py-1 rounded-full border font-medium"
                    style={{ background: el.level?.color ? `${el.level.color}18` : '#f0fdf4', borderColor: el.level?.color ?? '#166534', color: el.level?.color ?? '#166534' }}>
                    {el.level?.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Ratings */}
          {expert.ratings?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">
                Client Ratings ({expert.ratings.length}) · Avg {expert.averageRating ?? '—'}★
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {expert.ratings.map((r, i) => (
                  <div key={i} className="bg-cream-50 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-amber-500 text-xs">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                      <span className="text-[10px] text-forest-400">{new Date(r.createdAt).toLocaleDateString('en-GH')}</span>
                    </div>
                    {r.review && <p className="text-xs text-forest-600 italic">"{r.review}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Previous admin notes */}
          {expert.adminNotes && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest mb-1">Previous Admin Notes</p>
              <p className="text-sm text-amber-800">{expert.adminNotes}</p>
            </div>
          )}

          {/* Action notes */}
          {['PENDING', 'REVIEWING'].includes(expert.expertStatus) && (
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1.5">
                Decision Notes <span className="text-forest-400">(sent to expert via email + SMS)</span>
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
                placeholder="Add notes about your approval or rejection decision..."
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
            </div>
          )}

          {/* Buttons */}
          {['PENDING', 'REVIEWING'].includes(expert.expertStatus) && (
            <div className="flex gap-3 pt-2">
              <button onClick={() => handle('review')} disabled={!!acting}
                className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors font-medium disabled:opacity-50">
                {acting === 'review' ? 'Saving...' : <><Eye size={14} /> Mark Reviewing</>}
              </button>
              <button onClick={() => handle('approve')} disabled={!!acting}
                className="flex items-center gap-2 btn-primary text-sm py-2.5 disabled:opacity-50">
                {acting === 'approve' ? 'Approving...' : <><CheckCircle2 size={14} /> Approve</>}
              </button>
              <button onClick={() => handle('reject')} disabled={!!acting || !notes.trim()}
                className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-full bg-rust-500 text-white hover:bg-rust-600 transition-colors font-medium disabled:opacity-50">
                {acting === 'reject' ? 'Rejecting...' : <><XCircle size={14} /> Reject</>}
              </button>
            </div>
          )}
          {acting === 'reject' && !notes.trim() && (
            <p className="text-xs text-rust-500">Please add rejection notes before rejecting.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminExpertsPage = () => {
  const [tab, setTab] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const params = { page: 1, limit: 20 };
  if (tab !== 'ALL') params.status = tab;
  if (appliedSearch) params.search = appliedSearch;

  const { data, loading, error, refetch } = useApi(
    () => adminService.getAllExperts(params),
    [tab, appliedSearch],
  );

  // DEBUG — remove once confirmed working
  React.useEffect(() => {
    if (data !== null) {
      console.log('[AdminExpertsPage] raw data from API:', data);
    }
  }, [data]);
  // Handle all possible backend response shapes:
  // { experts: [...], total: N }  |  { data: [...] }  |  [...]  |  { meta: { total } }
  const experts = Array.isArray(data)
    ? data
    : Array.isArray(data?.experts)
      ? data.experts
      : Array.isArray(data?.data)
        ? data.data
        : [];
  const total = data?.total ?? data?.meta?.total ?? experts.length;

  const handleAction = async (expertId, action, notes) => {
    try {
      if (action === 'review')        await adminService.markReviewing(expertId);
      else if (action === 'approve')  await adminService.approveExpert(expertId, notes);
      else if (action === 'reject')   await adminService.rejectExpert(expertId, notes);
      toast.success(action === 'approve' ? 'Expert approved!' : action === 'reject' ? 'Expert rejected.' : 'Marked as reviewing.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
      throw err;
    }
  };

  const handleDisable = async (expert) => {
    const isDisabled = expert.user?.status === 'SUSPENDED';
    const action     = isDisabled ? 'enable' : 'disable';
    if (!window.confirm(`${isDisabled ? 'Re-enable' : 'Disable'} ${expert.user?.firstName} ${expert.user?.lastName}?`)) return;
    try {
      if (isDisabled) await adminService.enableExpert(expert.id);
      else            await adminService.disableExpert(expert.id);
      toast.success(`Expert ${isDisabled ? 're-enabled' : 'disabled'} successfully`);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async (expert) => {
    if (!window.confirm(`Remove ${expert.user?.firstName} ${expert.user?.lastName} as an expert? They will become a student and can still log in.`)) return;
    try {
      await adminService.deleteExpert(expert.id);
      toast.success('Expert removed — account is now a student');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete');
    }
  };

  const handleOnlineToggle = async (expert) => {
    try {
      await adminService.toggleOnlineStatus(expert.id);
      toast.success(`${expert.user?.firstName} marked as ${expert.isOnline ? 'Offline' : 'Online'}`);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update online status');
    }
  };

  return (
    <DashboardLayout title="Expert Management">
      {selected && <ExpertModal expert={selected} onClose={() => setSelected(null)} onAction={handleAction} />}
      <div className="space-y-5 max-w-5xl">

        {/* Search + tabs */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
            <input type="text" placeholder="Search by name or email..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setAppliedSearch(search)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white" />
          </div>
          <button onClick={() => setAppliedSearch(search)} className="btn-primary text-sm py-2.5 px-5">Search</button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-xs font-medium px-4 py-2 rounded-full border transition-all ${tab === t ? 'bg-forest-800 text-cream-50 border-forest-800' : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'}`}>
              {t}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load experts.</span>
          </div>
        )}

        <p className="text-sm text-forest-500">{loading ? 'Loading...' : `${total} expert${total !== 1 ? 's' : ''}`}</p>

        {/* Expert list */}
        <div className="space-y-3">
          {loading ? Array.from({length: 4}).map((_, i) => (
            <div key={i} className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse h-20" />
          )) : experts.map(expert => {
            const name = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
            const specs = Array.isArray(expert.specializations) ? expert.specializations.slice(0, 2) : [];
            return (
              <div key={expert.id} className="bg-white border border-cream-200 rounded-2xl p-5 flex items-center gap-4 hover:shadow-sm transition-shadow">
                <div className="w-11 h-11 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold flex-shrink-0">
                  {expert.user?.avatar
                    ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover rounded-xl" />
                    : name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sans font-semibold text-forest-900 text-sm">{name}</span>
                    <span className={`text-[10px] font-medium border px-2 py-0.5 rounded-full ${statusStyle[expert.expertStatus] ?? ''}`}>
                      {expert.expertStatus}
                    </span>
                    {expert.isOnline && (
                      <span className="flex items-center gap-1 text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        Online
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-forest-500 mt-0.5">{expert.user?.email}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-forest-400">
                    {expert.user?.city && <span className="flex items-center gap-1"><MapPin size={10} />{expert.user.city}</span>}
                    <span className="flex items-center gap-1"><Clock size={10} />{expert.yearsOfExperience} yrs</span>
                    {expert.averageRating && <span className="flex items-center gap-1"><Star size={10} className="fill-gold-400 text-gold-400" />{Number(expert.averageRating).toFixed(1)}</span>}
                    {specs.length > 0 && <span>{specs.join(' · ')}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => setSelected(expert)}
                    className="flex items-center gap-1.5 text-xs font-medium text-forest-600 hover:text-forest-900 border border-cream-200 hover:border-forest-400 px-3 py-2 rounded-xl transition-all">
                    Review <ChevronRight size={13} />
                  </button>
                  {/* Online / Offline toggle */}
                  <button
                    onClick={() => handleOnlineToggle(expert)}
                    title={expert.isOnline ? 'Mark as Offline' : 'Mark as Online'}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-xl border transition-all ${
                      expert.isOnline
                        ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                        : 'border-cream-200 text-forest-400 hover:border-green-300 hover:text-green-600 hover:bg-green-50'
                    }`}>
                    {expert.isOnline ? <><Wifi size={13} /> Online</> : <><WifiOff size={13} /> Offline</>}
                  </button>
                  {/* Disable / Enable */}
                  <button
                    onClick={() => handleDisable(expert)}
                    title={expert.user?.status === 'SUSPENDED' ? 'Re-enable Expert' : 'Disable Expert'}
                    className={`p-2 rounded-xl border transition-all ${
                      expert.user?.status === 'SUSPENDED'
                        ? 'border-forest-200 text-forest-600 hover:bg-forest-50'
                        : 'border-amber-200 text-amber-600 hover:bg-amber-50'
                    }`}>
                    {expert.user?.status === 'SUSPENDED'
                      ? <UserCheck size={14} />
                      : <Ban size={14} />}
                  </button>
                  {/* Delete */}
                  <button
                    onClick={() => handleDelete(expert)}
                    title="Delete Expert"
                    className="p-2 rounded-xl border border-rust-200 text-rust-500 hover:bg-rust-50 transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && experts.length === 0 && (
          <div className="text-center py-16 text-forest-400 bg-white rounded-2xl border border-cream-200">
            <p className="font-serif text-xl text-forest-600">No experts found</p>
            <p className="text-sm mt-2">Try a different filter or search term</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminExpertsPage;